import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ── TS mirrors of GET /cases/{id}/production-tracker (snake_case verbatim) ──

/** One typed entry of the firm's DATE+Bates cell (RfpItem.bates_ranges). */
export interface BatesRangeEntry {
  start: string;
  end: string;
  date: string | null;
  supplement: number;
  note: string | null;
}

/** Day-grain MISSING math for one item (null = refer/inspection/no window). */
export interface TrackerItemMissing {
  window_start: string;
  window_end: string | null; // null = rolling "…– current"
  intervals: [string, string][];
  display: string;
  days_missing: number;
  complete: boolean;
  /** Succession-seam suppression/attribution sentences (P4). */
  seam_notes?: string[];
  /** Multi-account item: intervals are the UNION of per-link missing and
   * display is one labeled line per account (per-link math on accounts[]). */
  per_account?: boolean;
}

/** Item ↔ account link, resolved (or not) against current groups at read time. */
export interface TrackerAccountLink {
  bank_label: string | null;
  last4: string | null;
  owner_type: string | null;
  resolved: boolean;
  current_bank_label: string | null;
  chain_labels: string[] | null;
  /** Every chain member's last4 (root → terminal, deduped) — the gaps API
   * keys a chain's merged gap on the TERMINAL last4, so deep links must
   * carry all of them, not just the linked member's. */
  chain_last4s: string[] | null;
  /** Per-link MISSING math — present on multi-account items only. */
  missing?: TrackerItemMissing | null;
}

export interface TrackerItemDocument {
  document_id: number;
  filename: string;
  bates_range: string | null;
  supplement: number;
  produced_date: string | null;
}

/** The DATE column: union of typed entries and linked documents' arrivals. */
export interface TrackerItemEvent {
  date: string | null;
  supplement: number;
  range: string | null;
  source: 'typed' | 'document';
}

export type TrackerCoverageState = 'complete' | 'partial' | 'none' | 'n/a';

export interface TrackerItem {
  id: number;
  item_number: number;
  template_key: string | null;
  description: string;
  response_state: string | null; // null = unanswered
  refer_to: string | null;
  demand_start: string | null;
  demand_end: string | null;
  missing_note: string | null;
  notes: string | null;
  bates_ranges: BatesRangeEntry[];
  accounts: TrackerAccountLink[];
  documents: TrackerItemDocument[];
  events: TrackerItemEvent[];
  bates_display: string | null;
  missing: TrackerItemMissing | null;
  coverage_state: TrackerCoverageState;
}

export interface BatesFamily {
  prefix: string;
  width: number;
  span: string;
  observed: [number, number][];
  gaps: [string, string][];
  missing_line: string | null;
  padding_drift: boolean;
  document_count: number;
}

export interface RoundBates {
  families: BatesFamily[];
  conflicts: string[];
  unnumbered_documents: { document_id: number; filename: string }[];
}

export interface RoundSupplement {
  supplement: number;
  produced_date: string | null;
  document_count: number;
}

export interface TrackerRound {
  id: number;
  round_number: number;
  label: string | null;
  method: string;
  served_date: string | null;
  response_due_date: string | null;
  response_date: string | null;
  taxonomy_version: string | null;
  notes: string | null;
  supplements: RoundSupplement[];
  bates: RoundBates;
  items: TrackerItem[];
}

export type ProducingParty = 'client' | 'opposing' | 'third_party';

export interface TrackerParty {
  producing_party: ProducingParty;
  party_label: string;
  rounds: TrackerRound[];
}

export interface AccountSuccession {
  id: number;
  predecessor_bank_label: string;
  successor_bank_label: string;
  predecessor_last4: string | null;
  successor_last4: string | null;
  succession_date: string | null;
  reason: string;
  note: string | null;
  resolved: boolean;
}

export interface TrackerChainEdge {
  predecessor: string;
  successor: string;
  succession_date: string | null;
  reason: string;
}

export interface TrackerChain {
  labels: string[];
  root: string;
  terminal: string;
  edges: TrackerChainEdge[];
  unresolved: string[];
}

export interface TrackerUnassignedDocument {
  document_id: number;
  filename: string;
  source_party: ProducingParty;
  uploaded_at: string | null;
  bates_range: string | null;
}

export interface ProductionTracker {
  anchor: string | null;
  taxonomy: { version: string | null; item_count: number };
  parties: TrackerParty[];
  successions: AccountSuccession[];
  chains: TrackerChain[];
  unassigned_documents: TrackerUnassignedDocument[];
}

// ── request bodies ──────────────────────────────────────────────────────────

export interface RoundCreateBody {
  producing_party: ProducingParty;
  method?: string;
  label?: string | null;
  served_date?: string | null;
  response_due_date?: string | null;
  response_date?: string | null;
  seed_taxonomy?: boolean;
}

export interface RoundPatchBody {
  method?: string;
  label?: string | null;
  served_date?: string | null;
  response_due_date?: string | null;
  response_date?: string | null;
  notes?: string | null;
}

export interface ItemCreateBody {
  item_number?: number | null;
  description: string;
  response_state?: string | null;
}

export interface ItemPatchBody {
  item_number?: number;
  description?: string;
  response_state?: string | null;
  refer_to?: string | null;
  bates_ranges?: Omit<BatesRangeEntry, 'note'>[] | BatesRangeEntry[];
  demand_start?: string | null;
  demand_end?: string | null;
  missing_note?: string | null;
  notes?: string | null;
}

export interface DocumentRoundAssignBody {
  round_id: number | null;
  produced_date?: string | null;
  supplement?: number | null;
}

export interface SuccessionCreateBody {
  predecessor_bank_label: string;
  successor_bank_label: string;
  succession_date?: string | null;
  reason?: string;
  note?: string | null;
}

// ── the aggregate query ─────────────────────────────────────────────────────

export function useProductionTracker(caseId: string | undefined) {
  return useQuery<ProductionTracker>({
    queryKey: ['production-tracker', caseId],
    queryFn: async () => {
      const { data } = await api.get(`/cases/${caseId}/production-tracker`);
      return data;
    },
    enabled: !!caseId,
  });
}

// ── mutations ───────────────────────────────────────────────────────────────
// Every mutation invalidates ['production-tracker', caseId]; the document-round
// assignment also ['documents', caseId] (round chips in the Documents tree);
// succession mutations also ['missing-gaps', caseId] + ['accounts', caseId]
// (chain-aware detectors and the AccountsTab "Reissued →" tag both shift).

function useTrackerInvalidate(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
}

export function useCreateRound(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async (body: RoundCreateBody) => {
      const { data } = await api.post(`/cases/${caseId}/production-rounds`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function usePatchRound(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async ({ roundId, body }: { roundId: number; body: RoundPatchBody }) => {
      const { data } = await api.patch(`/production-rounds/${roundId}`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteRound(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async (roundId: number) => {
      const { data } = await api.delete(`/production-rounds/${roundId}`);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useCreateItem(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async ({ roundId, body }: { roundId: number; body: ItemCreateBody }) => {
      const { data } = await api.post(`/production-rounds/${roundId}/items`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function usePatchItem(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async ({ itemId, body }: { itemId: number; body: ItemPatchBody }) => {
      const { data } = await api.patch(`/rfp-items/${itemId}`, body);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteItem(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async (itemId: number) => {
      const { data } = await api.delete(`/rfp-items/${itemId}`);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useSetItemAccounts(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async ({ itemId, bankLabels }: { itemId: number; bankLabels: string[] }) => {
      const { data } = await api.put(`/rfp-items/${itemId}/accounts`, {
        bank_labels: bankLabels,
      });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useSetItemDocuments(caseId: string | undefined) {
  const invalidate = useTrackerInvalidate(caseId);
  return useMutation({
    mutationFn: async ({ itemId, documentIds }: { itemId: number; documentIds: number[] }) => {
      const { data } = await api.put(`/rfp-items/${itemId}/documents`, {
        document_ids: documentIds,
      });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useAssignDocumentRound(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentId,
      body,
    }: {
      documentId: number;
      body: DocumentRoundAssignBody;
    }) => {
      const { data } = await api.patch(
        `/documents/${documentId}/production-round`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
      queryClient.invalidateQueries({ queryKey: ['documents', caseId] });
    },
  });
}

export function useCreateSuccession(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SuccessionCreateBody) => {
      const { data } = await api.post(`/cases/${caseId}/account-successions`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
      queryClient.invalidateQueries({ queryKey: ['missing-gaps', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
    },
  });
}

export function useDeleteSuccession(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (successionId: number) => {
      const { data } = await api.delete(`/account-successions/${successionId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
      queryClient.invalidateQueries({ queryKey: ['missing-gaps', caseId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', caseId] });
    },
  });
}
